import type { FunctionComponent } from 'preact';
import { useCallback, useEffect, useState } from 'preact/hooks';

import {
  getIsolatedKeyFromWidgetName,
  isolatedDialog,
  isolatedHeader,
} from 'Addons/fitmentSearch/isolatedKeys';
import type { VehicleWidgetTemplateParams } from 'Components/fitmentSearch/vehicleWidget/vehicleWidget';
import type { TemplateFunction, TemplateFunctionInvoker, TemplateResult } from 'Components/types';
import type { FitmentFields } from 'Core/actions/fitmentSearch/fields';
import { isolatedDiscardVehicle, setFitmentFields, vehicleSelected } from 'Core/actions/fitmentSearch/index';
import {
  createAllRequiredFieldsHaveSelectionSelector,
  garageDataSelector,
  selectedVehicleSelector,
} from 'Core/selectors/fitmentSearch/index';
import { useDispatch, useSelector } from 'Core/store';
import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig';

import VehicleWidget from './vehicleWidget/vehicleWidget';

type Tab = {
  id: string;
  title: string;
  fields: FitmentFields;
};

interface Params {
  tab: string;
  tabs: Tab[];
  onSelectTab: (tab: string) => () => void;
  vehicleWidget: TemplateFunctionInvoker<VehicleWidgetTemplateParams>;
}

type Props = {
  template: TemplateFunction<Params>;
  name: string;
  tabs: Tab[];
  initCollapsed?: boolean;
  globalVehicleDiscardEnabled?: boolean;
  columnBreakpoint?: number;
  noStubSelect?: boolean;
  useNativeDropdown?: boolean;
};

const VehicleTabs: FunctionComponent<Props> = ({
  template,
  name,
  tabs,
  initCollapsed,
  globalVehicleDiscardEnabled,
  columnBreakpoint,
  noStubSelect,
  useNativeDropdown,
}) => {
  const dispatch = useDispatch();

  tabs.forEach((tab, idx) => {
    tab.id = `tab-${idx}`;
  });

  const { currentTab, setCurrentTab } = useTabs(tabs[0].id);

  const isolatedKey = getIsolatedKeyFromWidgetName(name);

  const isAllRequiredFieldHasSelection = useSelector(
    createAllRequiredFieldsHaveSelectionSelector(isolatedKey),
  ) as boolean;
  const selectedVehicle = useSelector(selectedVehicleSelector);
  const garageItems = useSelector(garageDataSelector);

  const getTabById = useCallback((id: string) => tabs[tabs.map((t) => t.id).indexOf(id)], [tabs]);
  const activeTabFields = getTabById(currentTab).fields;

  useEffect(() => {
    dispatch(setFitmentFields(activeTabFields, isolatedKey));
  }, [activeTabFields, dispatch, isolatedKey]);

  useEffect(() => {
    if (selectedVehicle.selection.length) {
      const tabsMap = tabs.map((tab) => ({
        ...tab,
        fields: Object.values(tab.fields).flat(),
      }));

      const tabIdByVehicle = tabsMap.filter((tab) =>
        selectedVehicle.fitmentValues.map((v) => v.field).every((f) => tab.fields.includes(f)),
      )[0]?.id;

      setCurrentTab(getTabById(tabIdByVehicle)?.id || tabs[0].id);
    }
  }, [getTabById, selectedVehicle, setCurrentTab, tabs]);

  const discardVehicle = () => {
    dispatch(isolatedDiscardVehicle(isolatedKey, globalVehicleDiscardEnabled));
    if (isAllRequiredFieldHasSelection) {
      fitmentSearchConfig.onVehicleDiscarded();
    }
  };

  const onSelectTab = (tab: string) => {
    setCurrentTab(tab);

    const currentTabFields = Object.values(getTabById(tab).fields).flat();
    const currentTabVehicle = garageItems
      .reverse()
      .find((vehicle) =>
        vehicle.fitmentValues.map((v) => v.field).every((f) => currentTabFields.includes(f)),
      );

    if (currentTabVehicle && isolatedKey !== isolatedDialog && isolatedKey !== isolatedHeader) {
      dispatch(vehicleSelected(currentTabVehicle, { isolatedKey }));
    } else {
      discardVehicle();
    }
  };

  const vehicleWidget: TemplateFunctionInvoker<VehicleWidgetTemplateParams> = (templ) => {
    const vehicleWidgetProps = {
      template: templ,
      name,
      fields: Object.values(activeTabFields).flat(),
      initCollapsed,
      isAlwaysActive: true,
      noStubSelect,
      columnBreakpoint,
      useNativeDropdown,
    };
    return (<VehicleWidget {...vehicleWidgetProps} key="VehicleWidget" />) as TemplateResult;
  };

  const component = template.call({
    tab: currentTab,
    tabs,
    onSelectTab: (tab) => () => onSelectTab(tab),
    vehicleWidget,
  });

  return component;
};

export default VehicleTabs;

function useTabs(initTab: string) {
  const [currentTab, setCurrentTab] = useState(initTab);

  return { currentTab, setCurrentTab };
}
